import {
  CalendarIcon,
  MapPinIcon as LocationMarkerIcon,
  ShieldCheckIcon,
  BriefcaseIcon,
  CurrencyDollarIcon,
} from "@heroicons/react/24/solid";
import advancedFormat from "dayjs/plugin/advancedFormat";
import dayjs from "dayjs";
import Link from "next/link";
import React, { useState } from "react";

import { getDepartmentLabel } from "../../lib/helpers";
import { getJob, getAllCareersWithSlug } from "../../lib/api";
import BlockContent from "../../components/block-content";
import Meta from "../../components/meta";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import { CareerApplicationForm } from "../../components/career-application-form";

dayjs.extend(advancedFormat);

const JobDetails = ({ data = {} }) => {
  const {
    location,
    // emails,
    department,
    closeDate,
    type,
    salaryRange,
    benefits,
    experienceRequirements,
    rolesResponsibilities,
  } = data;
  const details = [];
  const [open, setOpen] = useState(false);

  if (location) {
    details.push({
      title: "Location",
      value: location,
      icon: LocationMarkerIcon,
    });
  }
  if (department) {
    details.push({
      title: "Department",
      value: getDepartmentLabel(department),
      icon: BriefcaseIcon,
    });
  }
  if (type) {
    details.push({
      title: "Type",
      value: type,
      icon: ShieldCheckIcon,
    });
  }
  if (closeDate) {
    details.push({
      icon: CalendarIcon,
      title: "Closing On",
      value: dayjs(closeDate).format("[Closing on] MMMM YYYY"),
    });
  }
  if (salaryRange) {
    details.push({
      icon: CurrencyDollarIcon,
      title: "Salary Range",
      value: salaryRange,
    });
  }

  return (
    <div className="relative w-full">
      {data && data.title ? (
        <div className="space-y-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                <span className="sr-only">Details for </span>
                {data.title}
              </h2>

              <div className="mt-2">
                <div className="flex flex-col sm:flex-row sm:flex-wrap sm:c-gap sm:c-gap-x-4 sm:c-gap-y-2">
                  {details.map((i) => {
                    return (
                      <div
                        key={i.value}
                        className="mt-2 flex items-center text-sm text-gray-500"
                      >
                        <i.icon
                          className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400"
                          aria-hidden="true"
                        />
                        {i.value}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="flex flex-grow-0 md:mt-0 mt-4">
              {/* <a
                target="_blank"
                href={`mailto:${emails.join(",")}?subject=${encodeURIComponent(
                  `Application for ${data.title} in ${data.location}`
                )}`}
                rel="noreferrer"
                className="inline-flex flex-shrink-0 items-center px-4 py-2 border border-transparent text-sm font-medium rounded-full shadow-sm text-white bg-accent-600 hover:bg-accent-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500"
              >
                Apply for this job
              </a> */}

              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger className="inline-flex flex-shrink-0 items-center px-4 py-2 border border-transparent text-sm font-medium rounded-full shadow-sm text-white bg-accent-600 hover:bg-accent-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500">
                  Apply for this job
                </DialogTrigger>
                <DialogContent
                  className={
                    "lg:max-w-screen-sm overflow-y-scroll max-h-screen"
                  }
                >
                  <DialogHeader>
                    <DialogTitle>Apply for {data.title}</DialogTitle>
                    <DialogDescription>
                      Fill out the form below to apply for this job.
                    </DialogDescription>
                  </DialogHeader>

                  <CareerApplicationForm setOpen={setOpen} data={data} />
                </DialogContent>
              </Dialog>
            </div>
          </div>

          <div className="mt-6 border-t border-gray-100">
            <dl className="divide-y divide-gray-100">
              <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                <dt className="text-sm font-medium leading-6 text-gray-900">
                  Job Description
                </dt>
                <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                  <BlockContent
                    className="text-gray-700 prose-sm prose max-w-none"
                    content={data.description}
                  />
                </dd>
              </div>

              {rolesResponsibilities ? (
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                  <dt className="text-sm font-medium leading-6 text-gray-900">
                    Roles & Responsibilities
                  </dt>
                  <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                    <BlockContent
                      className="text-gray-700 prose-sm prose max-w-none"
                      content={rolesResponsibilities}
                    />
                  </dd>
                </div>
              ) : null}

              {experienceRequirements ? (
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                  <dt className="text-sm font-medium leading-6 text-gray-900">
                    Experience Requirements
                  </dt>
                  <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                    <BlockContent
                      className="text-gray-700 prose-sm prose max-w-none"
                      content={experienceRequirements}
                    />
                  </dd>
                </div>
              ) : null}

              {benefits ? (
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                  <dt className="text-sm font-medium leading-6 text-gray-900">
                    Benefits
                  </dt>
                  <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                    <BlockContent
                      className="text-gray-700 prose-sm prose max-w-none"
                      content={benefits}
                    />
                  </dd>
                </div>
              ) : null}
            </dl>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default function Careers({ data }) {
  const seo = data?.seo || {};
  const breadcrumbs = [{ id: 1, name: "Careers", href: "/careers" }];

  return (
    <div>
      <Meta path={`/careers?job=${data?.slug?.current}`} {...data} {...seo} />

      <div className="max-w-4xl mx-auto pt-8 pb-12 px-4 sm:px-6 lg:px-8">
        <nav aria-label="Breadcrumb" className="mb-2 hidden md:block">
          <ol role="list" className="flex items-center space-x-4">
            {breadcrumbs.map((breadcrumb) => (
              <li key={breadcrumb.id}>
                <div className="flex items-center">
                  <Link
                    href={breadcrumb.href}
                    className="mr-4 text-sm font-medium text-gray-500"
                  >
                    {breadcrumb.name}
                  </Link>
                  <svg
                    viewBox="0 0 6 20"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                    className="h-5 w-auto text-gray-300"
                  >
                    <path
                      d="M4.878 4.34H3.551L.27 16.532h1.327l3.281-12.19z"
                      fill="currentColor"
                    />
                  </svg>
                </div>
              </li>
            ))}
            <li className="text-sm">
              <Link
                href={`/careers/${data.slug && data.slug.current}`}
                aria-current="page"
                className="font-medium text-gray-500 hover:text-gray-600"
              >
                {data.title}
              </Link>
            </li>
          </ol>
        </nav>
        <JobDetails data={data} />
      </div>
    </div>
  );
}

export async function getStaticProps({ params, preview = false }) {
  const data = await getJob(params.slug, preview);
  return {
    props: { data },
    revalidate: 10,
  };
}

export async function getStaticPaths() {
  const allCareers = await getAllCareersWithSlug();
  return {
    paths:
      allCareers?.map((career) => ({
        params: {
          slug: career.slug,
        },
      })) || [],
    fallback: "blocking",
  };
}
